import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { updateUserPassword } from '../api/userApi';
import { get2FAStatus, setup2FA, verify2FA, disable2FA } from '../api/authApi';
import { toast } from 'react-toastify';
import { Key, Save, User, Shield, ShieldCheck, ShieldOff, SmartphoneCharging } from 'lucide-react';

const ProfilePage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});

  // 2FA durumunu kontrol et
  useEffect(() => {
    const check2FAStatus = async () => {
      setTwoFALoading(true);
      try {
        const response = await get2FAStatus();
        setIs2FAEnabled(response.is2FAEnabled);
      } catch (err) {
        console.error('2FA durumu kontrol edilirken hata:', err);
      } finally {
        setTwoFALoading(false);
      }
    };

    check2FAStatus();
  }, []);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm({ ...passwordForm, [name]: value });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Mevcut şifre gereklidir';
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'Yeni şifre gereklidir';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Şifre en az 6 karakter olmalıdır';
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await updateUserPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      toast.success('Şifre başarıyla güncellendi');
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      toast.error(err.message || 'Şifre güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // 2FA kurulumunu başlat
  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const response = await setup2FA();
      setSetup2FAData(response);
    } catch (err) {
      toast.error(err.message || '2FA kurulumu başlatılırken bir hata oluştu');
    } finally {
      setTwoFALoading(false);
    }
  };

  // 2FA doğrulama kodunu doğrula ve etkinleştir
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      toast.error('Doğrulama kodu gereklidir');
      return;
    }
    
    setTwoFALoading(true);
    try {
      await verify2FA(verificationCode);
      toast.success('İki faktörlü kimlik doğrulama başarıyla etkinleştirildi');
      setIs2FAEnabled(true);
      setSetup2FAData(null);
      setVerificationCode('');
    } catch (err) {
      toast.error(err.message || 'Doğrulama kodunu kontrol ederken bir hata oluştu');
    } finally {
      setTwoFALoading(false);
    }
  };

  // 2FA'yı devre dışı bırak
  const handleDisable2FA = async (e) => {
    e.preventDefault();
    
    if (!disablePassword) {
      toast.error('Şifre gereklidir');
      return;
    }
    
    setTwoFALoading(true);
    try {
      await disable2FA(disablePassword);
      toast.success('İki faktörlü kimlik doğrulama devre dışı bırakıldı');
      setIs2FAEnabled(false);
      setDisablePassword('');
    } catch (err) {
      toast.error(err.message || '2FA devre dışı bırakılırken bir hata oluştu');
    } finally {
      setTwoFALoading(false);
    }
  };

  if (!user) {
    return <div className="text-center p-8">Kullanıcı bilgileri yükleniyor...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Üst Bölüm - Kullanıcı Bilgileri */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center mb-6">
          <User className="h-10 w-10 text-primary-600 mr-4" />
          <h1 className="text-2xl font-bold text-gray-800">Profil Bilgileri</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Kullanıcı Adı</h3>
            <p className="text-lg font-medium">{user.username}</p>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Ad Soyad</h3>
            <p className="text-lg font-medium">{user.full_name || "-"}</p>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">E-posta</h3>
            <p className="text-lg font-medium">{user.email || "-"}</p>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">Yetki Seviyesi</h3>
            <p className="text-lg font-medium">{user.role_display_name || user.role_name || "-"}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">İki Faktörlü Kimlik Doğrulama</h3>
            <p className="text-lg font-medium flex items-center">
              {twoFALoading ? (
                <span className="text-gray-500 flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Kontrol ediliyor...
                </span>
              ) : is2FAEnabled ? (
                <span className="text-green-600 flex items-center">
                  <ShieldCheck className="h-5 w-5 mr-2" />
                  Etkin
                </span>
              ) : (
                <span className="text-red-600 flex items-center">
                  <ShieldOff className="h-5 w-5 mr-2" />
                  Devre Dışı
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Sekme Butonları */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 font-medium border-b-2 ${
            activeTab === 'profile'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('profile')}
        >
          <div className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            Şifre Değiştir
          </div>
        </button>
        <button
          className={`py-2 px-4 font-medium border-b-2 ${
            activeTab === '2fa'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('2fa')}
        >
          <div className="flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            İki Faktörlü Kimlik Doğrulama
          </div>
        </button>
      </div>

      {/* Şifre Değiştirme Bölümü */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-6">
            <Key className="h-8 w-8 text-primary-600 mr-4" />
            <h2 className="text-xl font-bold text-gray-800">Şifre Değiştir</h2>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-4 mb-6">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Mevcut Şifre
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-3 py-2 border ${errors.currentPassword ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
                />
                {errors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Yeni Şifre
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-3 py-2 border ${errors.newPassword ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
                />
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Yeni Şifre (Tekrar)
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full px-3 py-2 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Şifreyi Güncelle
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 2FA Bölümü */}
      {activeTab === '2fa' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-primary-600 mr-4" />
            <h2 className="text-xl font-bold text-gray-800">İki Faktörlü Kimlik Doğrulama</h2>
          </div>

          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              İki faktörlü kimlik doğrulama, hesabınıza ekstra bir güvenlik katmanı ekler. 
              Etkinleştirildiğinde, giriş yaparken şifrenize ek olarak mobil cihazınızdan bir doğrulama kodu girmeniz gerekecektir.
            </p>

            <div className="flex items-center text-gray-800 mb-2">
              <SmartphoneCharging className="h-5 w-5 mr-2 text-primary-600" />
              <span className="font-medium">Google Authenticator veya benzeri bir uygulama kullanın</span>
            </div>
          </div>

          {/* 2FA durumuna göre içeriği göster */}
          {is2FAEnabled ? (
            // 2FA devre dışı bırakma formu
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center mb-4">
                <ShieldOff className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="font-bold text-red-800">İki Faktörlü Kimlik Doğrulamayı Devre Dışı Bırak</h3>
              </div>

              <p className="text-red-700 mb-4">
                İki faktörlü kimlik doğrulamayı devre dışı bırakmak, hesabınızın güvenliğini azaltır. 
                Bu işlemi onaylamak için şifrenizi girmeniz gerekiyor.
              </p>

              <form onSubmit={handleDisable2FA} className="space-y-4">
                <div>
                  <label htmlFor="disablePassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Şifreniz
                  </label>
                  <input
                    type="password"
                    id="disablePassword"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={twoFALoading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:cursor-not-allowed"
                  >
                    {twoFALoading ? 'İşleniyor...' : 'Devre Dışı Bırak'}
                  </button>
                </div>
              </form>
            </div>
          ) : setup2FAData ? (
            // 2FA kurulum adımları
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center mb-4">
                <ShieldCheck className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="font-bold text-blue-800">İki Faktörlü Kimlik Doğrulama Kurulumu</h3>
              </div>

              <div className="mb-6">
                <p className="text-blue-700 mb-4">
                  Aşağıdaki QR kodu Google Authenticator veya benzer bir uygulama ile tarayın veya gizli anahtarı manuel olarak girin.
                </p>

                <div className="flex flex-col md:flex-row items-center justify-center md:space-x-8 mb-4">
                  <div className="mb-4 md:mb-0 flex justify-center">
                    <img 
                      src={setup2FAData.qrCode} 
                      alt="2FA QR Code" 
                      className="border border-gray-300 p-2 rounded"
                    />
                  </div>
                  
                  <div className="text-center md:text-left">
                    <p className="text-sm text-gray-700 mb-1">Manuel giriş için kod:</p>
                    <div className="bg-gray-100 border border-gray-300 p-2 rounded mb-4 font-mono text-sm select-all">
                      {setup2FAData.secret}
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div>
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Doğrulama Kodu
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    required
                    maxLength={6}
                    placeholder="6 haneli kodu girin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Uygulamanızda görüntülenen 6 haneli kodu girin
                  </p>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setSetup2FAData(null)}
                    disabled={twoFALoading}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    İptal
                  </button>
                  
                  <button
                    type="submit"
                    disabled={twoFALoading || verificationCode.length !== 6}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                  >
                    {twoFALoading ? 'Doğrulanıyor...' : 'Doğrula ve Etkinleştir'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            // 2FA kurulum başlatma
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="font-bold text-green-800">İki Faktörlü Kimlik Doğrulamayı Etkinleştir</h3>
              </div>

              <p className="text-green-700 mb-4">
                Hesabınızı korumak için Google Authenticator veya benzeri bir uygulama kullanarak iki faktörlü kimlik doğrulamayı etkinleştirin.
              </p>

              <button
                onClick={handleSetup2FA}
                disabled={twoFALoading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-300 disabled:cursor-not-allowed"
              >
                {twoFALoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Hazırlanıyor...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Shield className="mr-2 h-4 w-4" />
                    Kurulumu Başlat
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilePage; 